import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  alpha,
  Tooltip,
  useTheme,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TouchApp as TouchAppIcon
} from '@mui/icons-material';
import { Annotation, MacroImage, ImageBounds } from '../types';

interface MacroImageViewerProps {
  apiBaseUrl: string;
  selectedMacroImage: MacroImage | null;
  annotationMode: boolean;
  annotations: Annotation[];
  currentCoordinates: {x: number, y: number, width: number, height: number} | null;
  imageBounds: ImageBounds | null;
  macroImageRef: React.RefObject<HTMLDivElement>;
  isFollowupSession?: boolean;
  baselineContext?: any;
  onToggleAnnotationMode: () => void;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onSelectAnnotation: (annotation: Annotation) => void;
  onEditAnnotation: (annotation: Annotation) => void;
  onDeleteAnnotation: (annotation: Annotation) => void;
  onAddFollowupImage?: (annotation: Annotation) => void;
  isLoading?: boolean;
}


const MacroImageViewer: React.FC<MacroImageViewerProps> = ({
  apiBaseUrl,
  selectedMacroImage,
  annotationMode,
  annotations,
  currentCoordinates,
  imageBounds,
  macroImageRef,
  onToggleAnnotationMode,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onImageLoad,
  onSelectAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  isFollowupSession = false,
  baselineContext,
  isLoading=false,
  onAddFollowupImage
}) => {
  const theme = useTheme();

  if (!selectedMacroImage) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            No Macro Image Selected
          </Typography>
          <Typography color="text.secondary" paragraph>
            Please upload a macroscopic image to begin mole mapping.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 2 }}>
        {/* Annotation controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {selectedMacroImage.imageName || 'Macro Image'}
          </Typography>
          <Box>
            <Button
              variant={annotationMode ? "contained" : "outlined"}
              startIcon={annotationMode ? <CloseIcon /> : <AddIcon />}
              onClick={onToggleAnnotationMode}
              sx={{ mr: 1 }}
              disabled={isLoading}
            >
              {annotationMode ? 'Cancel' : 'Add Annotation'}
            </Button>
          </Box>
        </Box>
        
        {/* Annotation instructions */}
        {annotationMode && !isLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Click and drag on the image to create a new annotation
          </Alert>
        )}
        
        {/* Loading state */}
        {isLoading && (
          <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            Loading image and annotations...
          </Alert>
        )}
        
        {/* Image with annotations */}
        <Box 
          ref={macroImageRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: 500,
            bgcolor: 'background.paper',
            cursor: annotationMode && !isLoading ? 'crosshair' : 'default',
            overflow: 'hidden',
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block'
            }
          }}
          onMouseDown={!isLoading ? onMouseDown : undefined}
          onMouseMove={!isLoading ? onMouseMove : undefined}
          onMouseUp={!isLoading ? onMouseUp : undefined}
          onMouseLeave={annotationMode && !isLoading ? onMouseUp : undefined}
        >
          {isLoading ? (
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height="100%" 
              animation="wave"
              sx={{ 
                transform: 'none',
                borderRadius: 1
              }}
            />
          ) : (
            <img
              src={`${apiBaseUrl}${selectedMacroImage.imageUrl}`}
              alt={selectedMacroImage.imageName || 'Macro image'}
              onLoad={onImageLoad}
            />
          )}
          
          {/* Existing annotations - only show when not loading */}
          {!isLoading && annotations.map(annotation => {
            if (!imageBounds) return null;
            
            return (
              <Tooltip
                key={annotation.id}
                title={`${annotation.label}${annotation.linkedMicroId ? ' (Linked)' : ''}`}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${imageBounds.offsetX + (annotation.x * imageBounds.width)}px`,
                    top: `${imageBounds.offsetY + (annotation.y * imageBounds.height)}px`,
                    width: `${annotation.width * imageBounds.width}px`,
                    height: `${annotation.height * imageBounds.height}px`,
                    border: `2px solid ${annotation.linkedMicroId ? theme.palette.success.main : theme.palette.primary.main}`,
                    backgroundColor: alpha(
                      annotation.linkedMicroId ? theme.palette.success.main : theme.palette.primary.main, 
                      0.2
                    ),
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(
                        annotation.linkedMicroId ? theme.palette.success.main : theme.palette.primary.main, 
                        0.3
                      ),
                      '& .annotation-actions': {
                        opacity: 1
                      }
                    }
                  }}
                  onClick={() => onSelectAnnotation(annotation)}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -24,
                      left: 0,
                      backgroundColor: annotation.linkedMicroId ? theme.palette.success.main : theme.palette.primary.main,
                      color: '#fff',
                      px: 1,
                      py: 0.5,
                      borderRadius: '4px 4px 0 0',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {annotation.label}
                    {annotation.linkedMicroId && (
                      <LinkIcon sx={{ fontSize: '0.75rem', ml: 0.5 }} />
                    )}
                  </Box>
                  
                  {/* Annotation action buttons */}
                  <Box 
                    className="annotation-actions"
                    sx={{
                      position: 'absolute',
                      top: -36,
                      right: -40,
                      display: 'flex',
                      gap: 0.5,
                      opacity: 0,
                      transition: 'opacity 0.2s ease'
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.info.main, 0.9),
                        color: '#fff',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          backgroundColor: theme.palette.info.main
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditAnnotation(annotation);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.error.main, 0.9),
                        color: '#fff',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          backgroundColor: theme.palette.error.main
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteAnnotation(annotation);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
          
          {/* Current annotation being drawn - only show when not loading */}
          {!isLoading && annotationMode && currentCoordinates && (() => {
            if (!imageBounds) return null;
            
            const normalizedCoords = {
              x: Math.min(currentCoordinates.x, currentCoordinates.x + currentCoordinates.width),
              y: Math.min(currentCoordinates.y, currentCoordinates.y + currentCoordinates.height),
              width: Math.abs(currentCoordinates.width),
              height: Math.abs(currentCoordinates.height)
            };
            
            return (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${imageBounds.offsetX + (normalizedCoords.x * imageBounds.width)}px`,
                  top: `${imageBounds.offsetY + (normalizedCoords.y * imageBounds.height)}px`,
                  width: `${normalizedCoords.width * imageBounds.width}px`,
                  height: `${normalizedCoords.height * imageBounds.height}px`,
                  border: `2px dashed ${theme.palette.primary.main}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }}
              />
            );
          })()}
        </Box>

        {/* Annotations List Section */}
        {annotations.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TouchAppIcon color="primary" />
                Annotations ({annotations.length}) - sorted by time created
                {isLoading && <CircularProgress size={16} />}
              </Typography>
              
              {isLoading ? (
                <Box>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton 
                      key={index}
                      variant="rectangular" 
                      height={60} 
                      sx={{ mb: 1, borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <List dense>
                  {annotations.map((annotation, index) => (
                    <ListItem
                      key={annotation.id}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => onSelectAnnotation(annotation)}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {annotation.label}
                            </Typography>
                            {annotation.linkedMicroId && (
                              <Chip
                                size="small"
                                icon={<LinkIcon />}
                                label="Linked"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            Position: ({Math.round(annotation.x * 100)}%, {Math.round(annotation.y * 100)}%) • 
                            Size: {Math.round(annotation.width * 100)}% × {Math.round(annotation.height * 100)}%
                            {annotation.confidence && ` • Confidence: ${Math.round(annotation.confidence * 100)}%`}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {/* Add follow-up image button for follow-up sessions */}
                          {isFollowupSession && !baselineContext && onAddFollowupImage && (
                            <Tooltip title="Add follow-up dermatoscopic image">
                              <IconButton
                                size="small"
                                color="success"
                                disabled={isLoading}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddFollowupImage(annotation);
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {/* Always show these buttons */}
                          <IconButton
                            size="small"
                            color="info"
                            disabled={isLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditAnnotation(annotation);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteAnnotation(annotation);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Click on an annotation to view or add dermatoscopic images. 
                {annotations.some(a => a.linkedMicroId) && ' Green annotations have linked dermatoscopic images.'}
                {isFollowupSession && ' Use the + button to add follow-up images to existing annotations.'}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MacroImageViewer;

